import { AdvisoryCallStatus, AdvisoryFindingType } from './types'
import type {
  AdvisoryAiReportDetail,
  AdvisoryExport,
  AdvisoryFinding,
  AdvisoryFindingTypeValue,
} from './types'

/**
 * A prep that does not exist.
 *
 * The app tour walks through the four-step wizard, which needs a meeting to
 * walk through. A brand-new user has none — and the tour used to answer that by
 * silently skipping the half of the product it exists to explain.
 *
 * So the tour offers a sample instead: the real wizard, real layout, invented
 * contents. The id is what makes that work — every report-scoped call in
 * `api.ts` checks for it and answers from here, so a sample prep never reaches
 * the network and can never be mistaken for a real one.
 *
 * Nothing here is persisted, and nothing here is a fixture for tests: it is
 * user-facing content, and it is written to read like a real quarter.
 */

export const SAMPLE_CALL_ID = 'sample'

export const isSampleCall = (callId?: string | null) => callId === SAMPLE_CALL_ID

/** What a write to the sample answers with, rather than reaching the server. */
export class SampleCallError extends Error {
  constructor() {
    super('This is a sample prep, so nothing here is saved. Create a meeting to run a real one.')
    this.name = 'SampleCallError'
  }
}

const AGENDA = [
  {
    advisoryAgendaId: 'sample-agenda-1',
    title: 'Cash position and runway',
    description: 'Where cash landed against forecast, and how long it lasts at the current burn.',
    createdAt: '2026-01-05T09:00:00+00:00',
  },
  {
    advisoryAgendaId: 'sample-agenda-2',
    title: 'Margin by revenue stream',
    description: 'Which lines carried the quarter and which quietly cost money to serve.',
    createdAt: '2026-01-05T09:00:00+00:00',
  },
  {
    advisoryAgendaId: 'sample-agenda-3',
    title: 'Debtors and collections',
    description: 'Ageing, the concentration risk in the top accounts, and what to chase first.',
    createdAt: '2026-01-05T09:00:00+00:00',
  },
]

const finding = (item: {
  id: string
  agendaId: string
  type: AdvisoryFindingTypeValue
  heading: string
  description: string
}): AdvisoryFinding => ({
  advisoryFindingId: item.id,
  advisoryCallId: SAMPLE_CALL_ID,
  advisoryAgendaId: item.agendaId,
  type: item.type,
  heading: item.heading,
  description: item.description,
  createdAt: '2026-01-05T09:12:00+00:00',
})

export const SAMPLE_REPORT: AdvisoryAiReportDetail = {
  advisoryCallId: SAMPLE_CALL_ID,
  reports: ['ProfitAndLoss', 'BalanceSheet', 'AgedReceivables'],
  startDate: '2025-10-01T00:00:00+00:00',
  endDate: '2025-12-31T00:00:00+00:00',
  meetingDate: '2026-01-14T10:00:00+00:00',
  meetingTitle: 'Quarterly review — sample',
  createdAt: '2026-01-05T09:00:00+00:00',
  status: AdvisoryCallStatus.COMPLETED_BRIEF,
  exportType: null,
  summary:
    'A solid quarter on revenue, with the gain concentrated in one stream and the cost of serving it rising faster than the income. Cash is comfortable; collections are not.',
  notes: null,
  internalMemory: null,
  includeHeadlinerSummary: true,
  headlinerSummary: 'Revenue up 12%, margin down 3 points, and 41% of debtors now past 60 days.',
  agendaItems: AGENDA,
  findings: [
    finding({
      id: 'sample-finding-1',
      agendaId: 'sample-agenda-1',
      type: AdvisoryFindingType.METRICS,
      heading: 'Closing cash $412,800',
      description:
        'Up $38,400 on the September quarter, and ahead of the forecast $390,000. At the current net burn that is roughly nine months of runway.',
    }),
    finding({
      id: 'sample-finding-2',
      agendaId: 'sample-agenda-2',
      type: AdvisoryFindingType.FINDINGS,
      heading: 'Services margin has slipped 3 points',
      description:
        'Revenue grew 12% but gross margin fell from 46% to 43%. Almost all of the growth came from the services line, which carries the lowest margin and the most delivery hours.',
    }),
    finding({
      id: 'sample-finding-3',
      agendaId: 'sample-agenda-3',
      type: AdvisoryFindingType.FINDINGS,
      heading: '41% of debtors are past 60 days',
      description:
        'Receivables of $186,000, of which $76,000 is over 60 days. Two accounts make up more than half of that balance.',
    }),
    finding({
      id: 'sample-finding-4',
      agendaId: 'sample-agenda-3',
      type: AdvisoryFindingType.RECOMMENDATIONS,
      heading: 'Put the two largest overdue accounts on a payment plan',
      description:
        'Agree dates this month rather than issuing another statement. Between them they are the difference between a comfortable quarter and a tight one.',
    }),
  ],
}

export const SAMPLE_EXPORTS: AdvisoryExport[] = []
